import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Booking from '@solvhealth/types/interfaces/Booking';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { colors } from '../../../../../constants/colors';
import ClinicPhoto from '../../../../SolvPatternLibrary/ClinicPhoto';
import { emptyFunction } from '../../../../../core/util/function';
import useFetchSingleProvider from '../../../../ProviderGroup/hooks/useFetchSingleProvider';
import {
  isBookingCoveredByMembership,
  isPremiumBooking,
  isProviderGroupBooking,
  isTelemedBooking,
} from '../../../../../core/util/booking';
import { isEmpty } from '../../../../../core/util/empty';
import { ClinicOrProviderName } from '../../../../../components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/components/ClinicOrProviderName';
import ProviderPhoto from '../../../../ProviderGroup/components/ProviderPhoto';

interface IconButtonInterface {
  expanded?: string;
}

interface PhotoTimePlaceContainerInterface {
  $collapsible?: boolean;
}

const Root = styled.div.withConfig({ componentId: 'sc-1vo09z2-0' })`
  padding: 12px 16px;
`;

const ToggleContainer = styled.div.withConfig({ componentId: 'sc-1vo09z2-1' })`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  margin-top: 2px;
`;

const SizedClinicPhoto = styled(ClinicPhoto).withConfig({ componentId: 'sc-1vo09z2-2' })`
  width: 100%;
  height: 44px;
  max-width: 44px;
  flex-grow: 0;
`;

const SolvPlusImage = styled.img.withConfig({ componentId: 'sc-1vo09z2-3' })`
  height: 44px;
  max-width: 44px;
  border-radius: 10000px;
`;

const EmptyImage = styled.div.withConfig({ componentId: 'sc-1vo09z2-4' })`
  width: 100%;
  height: 44px;
  max-width: 44px;
  flex-grow: 0;
  border-radius: 50%;
  background-color: ${
    // @ts-expect-error ts-migrate(2551) FIXME: Property 'greyDim' does not exist on type '{ fadeT... Remove this comment to see the full error message
    colors.greyDim
  };
`;

const PhotoTimePlaceContainer = styled.div.withConfig({ componentId: 'sc-1vo09z2-5' })<PhotoTimePlaceContainerInterface>`
  display: flex;
  align-items: center;
  ${(props) =>
    props.$collapsible &&
    css`
      cursor: pointer;
    `}
`;

const TimePlaceContainer = styled.div.withConfig({ componentId: 'sc-1vo09z2-6' })`
  flex-grow: 1;
  padding-left: 10px;
`;

const AppointmentTime = styled.div.withConfig({ componentId: 'sc-1vo09z2-7' })`
  color: ${colors.lightText};
  font-size: ${fontSize.mediumSmall};
  font-family: ${fontFamily.book};
`;

const Toggle = styled(IconButton).withConfig({ componentId: 'sc-1vo09z2-8' })<IconButtonInterface>`
  transition: transform 200ms ease;
  transform: rotate(${({ expanded }) => (expanded ? '0deg' : '180deg')});
  padding: 0;
  path {
    color: ${colors.lightText};
  }
`;

const COLLAPSE_ENTERED_CLASS = 'collapse-entered';
const CollapseContainer = styled(Collapse).withConfig({ componentId: 'sc-1vo09z2-9' })`
  padding-top: 12px;
  overflow: hidden;
  & .${COLLAPSE_ENTERED_CLASS} {
    overflow: hidden;
  }
`;

type Props = {
  appointmentDate: string | null;
  collapsible?: boolean;
  location?: any;
  className?: string;
  booking: Booking;
  children?: any;
};

const VisitDetails = ({
  collapsible = true,
  appointmentDate,
  location,
  className,
  booking,
  children,
}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { provider } = useFetchSingleProvider(booking.provider_id);

  const handleClickExpand = () => {
    setExpanded(!expanded);
  };

  if (isProviderGroupBooking(booking) && isEmpty(provider)) {
    return null;
  }

  return (
    <Root className={className}>
      <PhotoTimePlaceContainer $collapsible={collapsible}>
        {(() => {
          if (isProviderGroupBooking(booking) && provider) {
            return <ProviderPhoto provider={provider} />;
          }

          if (isTelemedBooking(booking) && isPremiumBooking(booking)) {
            if (isBookingCoveredByMembership(booking)) {
              return <SolvPlusImage src="/images/account/camera_teal.png" />;
            }
            return <SolvPlusImage src="/images/account/camera_purple.png" />;
          }

          if (location) {
            return <SizedClinicPhoto location={location} />;
          }
          return <EmptyImage />;
        })()}

        <TimePlaceContainer onClick={collapsible && children ? handleClickExpand : emptyFunction}>
          <ClinicOrProviderName booking={booking} provider={provider} />

          <ToggleContainer>
            <AppointmentTime>{appointmentDate}</AppointmentTime>
            {collapsible && children && (
              <Toggle
                aria-expanded={expanded}
                aria-label="Show more"
                expanded={(!expanded).toString()}
              >
                <ExpandMoreIcon />
              </Toggle>
            )}
          </ToggleContainer>
        </TimePlaceContainer>
      </PhotoTimePlaceContainer>

      <CollapseContainer
        classes={{ entered: COLLAPSE_ENTERED_CLASS }}
        in={expanded}
        timeout="auto"
        unmountOnExit
      >
        {children}
      </CollapseContainer>
    </Root>
  );
};

export default VisitDetails;
